import React, { ReactNode, useRef, useState } from 'react';
import {
  ButtonSecondary,
  ButtonTertiary,
  cssColorValue,
  IconPublic,
  semanticColors,
  spacer24,
  Tooltip,
  TooltipTrigger,
} from '@spotify-internal/encore-web';
import styled from 'styled-components';
import { useRouter } from 'next/router';
import { overlayDirections } from '@spotify-internal/encore-web/core/styles';

import { pathWithoutMarket } from 'utils/market/market';
import { useTranslation } from 'providers/translations/TranslationProvider';
import { MastheadContainer } from 'components/Masthead/Header/types';
import { useServerSideConstants } from 'providers/ServerSideConstantsProvider';

const MobileTrigger = styled(ButtonSecondary)`
  &,
  &:hover,
  &:focus,
  &:focus:after {
    border-color: ${cssColorValue(semanticColors.essentialSubdued)};
    color: ${cssColorValue(semanticColors.textBase)};
    font-size: 14px;
    padding-inline: 55px ${spacer24};
    padding-block: 13px;
  }
`;

const DesktopTrigger = styled(ButtonTertiary)`
  cursor: pointer;

  &,
  &:hover,
  &:focus,
  &:focus:after {
    border-color: ${cssColorValue(semanticColors.textBase)};
    color: ${cssColorValue(semanticColors.textBase)};
  }
`;

const LanguageTooltip = ({ children, tooltipText }: { children: ReactNode; tooltipText: string }) => {
  const [enabled, setEnabled] = useState(false);

  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const onMouseEnter = () => {
    timer.current = setTimeout(() => {
      setEnabled(true);
    }, 1000);
  };

  const onMouseLeave = () => {
    if (timer.current) {
      clearTimeout(timer.current);
    }
    setEnabled(false);
  };

  return (
    <TooltipTrigger
      overlay={enabled && <Tooltip>{tooltipText}</Tooltip>}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      placement={overlayDirections.bottomStart}
      onShow={() => {}}
      onHide={() => {}}
    >
      {children}
    </TooltipTrigger>
  );
};

type Props = {
  container: MastheadContainer;
};

export const NavBarLanguagePicker = ({ container }: Props) => {
  const { t } = useTranslation();
  const tooltipText = t('choose_language_prompt');
  const label = t('change_language_label');

  const {
    market: { dir, displayNameWithLanguage },
  } = useServerSideConstants();
  const marketSelectReturnToPath = pathWithoutMarket(useRouter().asPath);

  const hrefQueryString = marketSelectReturnToPath
    ? `?returnToPath=${encodeURIComponent(marketSelectReturnToPath)}`
    : '';

  const href = `https://www.spotify.com/select-your-country-region/${hrefQueryString}`;

  if (container === 'header') {
    return (
      <LanguageTooltip tooltipText={tooltipText}>
        <DesktopTrigger
          aria-label={label}
          size="small"
          data-testid="language-selection-trigger-desktop"
          dir={dir}
          href={href}
          iconOnly={IconPublic}
        />
      </LanguageTooltip>
    );
  }

  return (
    <MobileTrigger
      aria-label={label}
      data-testid="language-selection-trigger-mobile"
      dir={dir}
      href={href}
      iconLeading={IconPublic}
    >
      {displayNameWithLanguage}
    </MobileTrigger>
  );
};
